import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";

const ResetPassword = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  // States
  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false,
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordResponse, setPasswordResponse] = useState({
    passwordResponseText:
      "Use 8 or more characters with a mix of letters, numbers, and symbols.",
    passwordResponseKey: "",
  });
  const [error, setError] = useState("");

  // Toggle visibility of password fields
  const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Password strength check (same as your existing logic)
  const onChangePassword = (pwd: string) => {
    setPassword(pwd);
    setError("");

    if (pwd.match(/^$|\s+/)) {
      setPasswordResponse({
        passwordResponseText:
          "Use 8 or more characters with a mix of letters, numbers & symbols",
        passwordResponseKey: "",
      });
    } else if (pwd.length === 0) {
      setPasswordResponse({
        passwordResponseText: "",
        passwordResponseKey: "",
      });
    } else if (pwd.length < 8) {
      setPasswordResponse({
        passwordResponseText: "Weak. Must contain at least 8 characters",
        passwordResponseKey: "0",
      });
    } else if (
      pwd.search(/[a-z]/) < 0 ||
      pwd.search(/[A-Z]/) < 0 ||
      pwd.search(/[0-9]/) < 0
    ) {
      setPasswordResponse({
        passwordResponseText:
          "Average. Must contain at least 1 upper case and number",
        passwordResponseKey: "1",
      });
    } else if (pwd.search(/(?=.*?[#?!@$%^&*-])/) < 0) {
      setPasswordResponse({
        passwordResponseText: "Almost. Must contain a special symbol",
        passwordResponseKey: "2",
      });
    } else {
      setPasswordResponse({
        passwordResponseText: "Awesome! You have a secure password.",
        passwordResponseKey: "3",
      });
    }
  };

  const onChangeConfirmPassword = (val: string) => {
    setConfirmPassword(val);
    setError("");
  };

  const { callApi, loading } = useApi("/api/createPassword", {
    method: "POST",
    autoCall: false,
  });

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Passwords do not match.",
        confirmButtonText: "OK",
      });
      return;
    }
    try {
      const payload = { newPassword: password };
      const response = await callApi(payload);
      if (response.statusCode === 201) {
        localStorage.removeItem("token");
      }
      Swal.fire({
        icon: "success",
        title: "Password Reset Successful",
        text: "Your password has been reset successfully. You can now log in with your new password.",
        showConfirmButton: true,
        confirmButtonText: "OK",
      });

      navigate(all_routes.resetPasswordSuccess);
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Password Reset Failed",
        text:
          error?.response?.data?.message ||
          "Unable to reset password at the moment. Please try again later.",
        showConfirmButton: true,
      });
    }
  };

  return (
    <div className="container-fuild">
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
              <div className="bg-overlay-img">
                <ImageWithBasePath
                  src="assets/img/bg/bg-01.png"
                  className="bg-1"
                  alt="Img"
                />
                <ImageWithBasePath
                  src="assets/img/bg/bg-02.png"
                  className="bg-2"
                  alt="Img"
                />
                <ImageWithBasePath
                  src="assets/img/bg/bg-03.png"
                  className="bg-3"
                  alt="Img"
                />
              </div>
              <div className="authentication-card w-100">
                <div className="authen-overlay-item border w-100">
                  <h1 className="text-white display-1">
                    WeConnect: Uniting teams, streamlining human resource.
                  </h1>
                  <div className="my-4 mx-auto authen-overlay-img">
                    <ImageWithBasePath
                      src="assets/img/weconnect_login.png"
                      alt="Img"
                    />
                  </div>
                  <div>
                    <p className="text-white fs-20 fw-semibold text-center">
                      Efficiently manage your workforce, streamline <br />{" "}
                      operations effortlessly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
              <div className="col-md-7 mx-auto vh-100">
                <form className="vh-100">
                  <div className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
                    <div className=" mx-auto text-center">
                      <ImageWithBasePath
                        src="/assets/img/logo/weconnectlogo.png"
                        className="img-fluid"
                        alt="Logo"
                      />
                    </div>
                    <div>
                      <div className="text-center mb-3">
                        <h2 className="mb-2">Reset Password</h2>
                        <p className="mb-0">
                          Your new password must be different from previous used
                          passwords.
                        </p>
                      </div>
                      <div>
                        <div className="input-block mb-3">
                          <div className="mb-3">
                            <label className="form-label">Password</label>
                            <div className="pass-group" id="passwordInput">
                              <input
                                type={
                                  passwordVisibility.password
                                    ? "text"
                                    : "password"
                                }
                                value={password}
                                onChange={(e) =>
                                  onChangePassword(e.target.value)
                                }
                                className="form-control pass-input"
                                placeholder="Enter your password"
                              />
                              <span
                                className={`ti toggle-passwords ${passwordVisibility.password
                                    ? "ti-eye"
                                    : "ti-eye-off"
                                  }`}
                                onClick={() =>
                                  togglePasswordVisibility("password")
                                }
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="input-block mb-3">
                          <label className="form-label">Confirm Password</label>
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.confirmPassword
                                  ? "text"
                                  : "password"
                              }
                              value={confirmPassword}
                              onChange={(e) =>
                                onChangeConfirmPassword(e.target.value)
                              }
                              className="pass-input form-control"
                              placeholder="Confirm your password"
                            />
                            <span
                              className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                                  ? "ti-eye"
                                  : "ti-eye-off"
                                }`}
                              onClick={() =>
                                togglePasswordVisibility("confirmPassword")
                              }
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </div>

                        {error && (
                          <div
                            className="text-danger mb-3"
                            style={{ fontWeight: "600" }}
                          >
                            {error}
                          </div>
                        )}

                        <div className="mb-3">
                          <button
                            type="submit"
                            onClick={handleSubmit}
                            className="btn btn-primary w-100"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 pb-4 text-center">
                      <p className="mb-0 text-gray-9">
                        © {new Date().getFullYear()} WeConnect. All rights reserved.
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
